/*
 * File: OrderFormPatientField.js
 * Project: mp-mobile-app
 *
 * Created by Jennifer Loecker on 10/28/2019
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Handles the orders form patient field. Gives the option of using a suggested patient, searching for a desired
 * patient, or creating a blank order.
 */
import React, { useState } from "react";
import PatientSearchDialog from "services/appointments/components/PatientSearchDialog";
import PatientIdStore from "services/patients/store/PatientIdStore";
import PropTypes from "prop-types";
import UIDialog from "components/ui/UIDialog";
import PatientSelectionCard from "services/appointments/components/PatientSelectionCard";
import { ListItem, Typography, List } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import UIStore from "../store/UI";
import { useStore } from "utils/useStore";
import { observer } from "mobx-react";

const styles = theme => ({
  searchButton: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    width: "100%"
  },
  buttonContainer: {
    marginTop: theme.spacing(2)
  }
});

const OrderFormPatientField = ({ populateIncidentOptions, order, classes }) => {
  const [showPatientSearch, setShowPatientSearch] = useState(false);
  const { showPatientSelection, togglePatientSelection } = useStore(UIStore);

  //Controls the patient search ability
  const togglePatientSearch = () => {
    setShowPatientSearch(!showPatientSearch);
  };

  //Populates suggested patients - if no suggested patients, directs to patient search ability
  const isPatientSelected = () => {
    const patientId = PatientIdStore.getId();
    if (patientId) return patientId > 1 && patientId;
    else setShowPatientSearch(true);
  };

  const closeForm = () => {
    togglePatientSearch();
    togglePatientSelection();
  };

  const patientSelectionResult = async patient => {
    if (patient.patientId !== order.patientId) {
      order.setPatient(patient);
      PatientIdStore.setId(patient.patientId);
    }
    populateIncidentOptions(patient.patientId);
    setShowPatientSearch(false);
  };

  const useSuggestedPatient = async patient => {
    patientSelectionResult(patient);
    togglePatientSelection();
  };

  return (
    <React.Fragment>
      {showPatientSelection && (
        <PatientSearchDialog
          showSearchDialog={showPatientSearch}
          toggleSearchDialog={closeForm}
          parentCallBack={patientSelectionResult}
        />
      )}
      <UIDialog
        open={showPatientSelection && !showPatientSearch}
        fullScreen={true}
        title="Orders"
        rightNav={() => <CloseIcon onClick={closeForm} color="inherit" />}
        content={() => (
          <List>
            {isPatientSelected() && (
              <PatientSelectionCard parentCallBack={useSuggestedPatient} />
            )}
            <ListItem
              button
              onClick={togglePatientSearch}
              className={classes.buttonContainer}
            >
              <Typography align="center" className={classes.searchButton}>
                Search for Patient
              </Typography>
            </ListItem>
            <ListItem
              button
              onClick={closeForm}
              className={classes.buttonContainer}
            >
              <Typography align="center" className={classes.searchButton}>
                Create Blank Order
              </Typography>
            </ListItem>
          </List>
        )}
      />
      {order.patientName && (
        <FormControl
          margin="normal"
          fullWidth
          component="fieldset"
          key="patientName"
        >
          <TextField
            fullWidth
            id="patientName"
            name="patientName"
            value={order.patientName}
            onClick={togglePatientSearch}
            variant="filled"
            label="Patient Name"
          />
        </FormControl>
      )}
    </React.Fragment>
  );
};

OrderFormPatientField.propTypes = {
  populateIncidentOptions: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(observer(OrderFormPatientField));
